console.log("NF COLLECTION SWITCHER LOADED v6 - MULTI SELECT + CLIENT PAGINATION");

/* ---------------------------------------------------------------
   How pagination now works:
   - Each selected filter (collection) may have more than one page
     of products on Shopify's side. We fetch ALL of a selected
     collection's pages, merge + dedupe every selected collection's
     products together, then paginate that merged list ourselves
     in chunks of NF_PAGE_SIZE (16).
   - Because we already hold the full merged list in memory,
     clicking a page number just re-slices the array - no network
     request needed.
   - The old theme pagination markup is never reused for merged
     results (it can't represent a merged/deduped set), we build
     our own instead.
   ------------------------------------------------------------- */

const NF_PAGE_SIZE = 16; // change this if your theme's "products per page" differs

let nfCurrentRequestId = 0;
let nfSelectedCollections = [];

// Cache for the current merged result set
let nfAllMergedItems = [];      // deduped raw <div class="grid__item"> elements
let nfMergedFallbackHtml = "";  // used only if #product-grid can't be found
let nfMergedCurrentPage = 1;

document.addEventListener("DOMContentLoaded", function () {
  nfCollectionMultiInit();
});

/* Block sort form submit */
document.addEventListener(
  "submit",
  function (e) {
    const form = e.target.closest("#FacetSortForm");
    if (!form) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    nfReloadCurrentSelection(true, 1);
  },
  true
);

/* Handle sorting ourselves */
document.addEventListener(
  "change",
  function (e) {
    const sortSelect = e.target.closest("#SortBy, .facet-filters__sort");
    if (!sortSelect) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    nfReloadCurrentSelection(true, 1);
  },
  true
);

/* Browser back / forward */
window.addEventListener("popstate", function () {
  nfLoadFromUrl(false);
});

function nfCollectionMultiInit() {
  const switcher = document.querySelector(".nf-collection-filter");
  if (!switcher) return;

  document.addEventListener(
    "click",
    function (e) {
      const collectionLink = e.target.closest("[data-nf-collection-link]");
      const resetButton = e.target.closest("[data-nf-reset-collection]");
      const openBtn = e.target.closest("[data-nf-open-filter]");
      const closeBtn = e.target.closest("[data-nf-close-filter]");

      if (collectionLink) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        nfToggleCollection(collectionLink);
        return;
      }

      if (resetButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        nfClearCollections(true);
        return;
      }

      if (openBtn) {
        e.preventDefault();
        nfOpenMobileFilter();
        return;
      }

      if (closeBtn) {
        e.preventDefault();
        nfCloseMobileFilter();
        return;
      }
    },
    true
  );

  nfLoadFromUrl(false);
}

function nfToggleCollection(link) {
  const handle = link.dataset.collectionHandle;
  const url = link.dataset.collectionUrl;

  if (!handle || !url) return;

  const exists = nfSelectedCollections.some(function (item) {
    return item.handle === handle;
  });

  if (exists) {
    nfSelectedCollections = nfSelectedCollections.filter(function (item) {
      return item.handle !== handle;
    });
  } else {
    nfSelectedCollections.push({
      handle: handle,
      url: url
    });
  }

  nfReloadCurrentSelection(true, 1);
}

function nfReloadCurrentSelection(pushUrl, page) {
  const sortBy = nfGetCurrentSortValue();

  if (nfSelectedCollections.length === 0) {
    nfLoadBaseCollection(pushUrl, sortBy);
    return;
  }

  nfLoadMultipleCollections(nfSelectedCollections, sortBy, pushUrl, page || 1);
}

function nfLoadBaseCollection(pushUrl, sortBy, page) {
  const switcher = document.querySelector(".nf-collection-filter");

  const baseUrl =
    switcher && switcher.dataset.baseCollectionUrl
      ? switcher.dataset.baseCollectionUrl
      : window.location.pathname;

  // Honor whatever page we were asked to load (defaults to whatever is in the
  // current URL, falling back to 1) instead of silently always loading page 1.
  const requestedPage = page || parseInt(new URLSearchParams(window.location.search).get("page"), 10) || 1;

  nfCurrentRequestId++;
  const requestId = nfCurrentRequestId;

  nfSetCollectionLoading(true);

  fetch(nfBuildCollectionFetchUrl(baseUrl, sortBy, requestedPage), {
    method: "GET",
    headers: {
      Accept: "text/html"
    }
  })
    .then(function (response) {
      if (!response.ok) throw new Error("Base collection fetch failed");
      return response.text();
    })
    .then(function (html) {
      if (requestId !== nfCurrentRequestId) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const freshGridContainer = doc.querySelector("#ProductGridContainer");

      if (!freshGridContainer) {
        throw new Error("Base #ProductGridContainer not found");
      }

      document.querySelector("#ProductGridContainer").innerHTML = freshGridContainer.innerHTML;

      // Base collection uses Shopify's own native pagination markup - leave it alone.
      nfTogglePagination();
      nfSetActiveCollections([]);
      nfToggleResetButton();
      nfSetSortValue(sortBy);
      nfReapplyGridColumns();

      if (pushUrl) {
        nfUpdateUrl([], sortBy, requestedPage);
      }
    })
    .catch(function (error) {
      console.error("NF base collection error:", error);
    })
    .finally(function () {
      if (requestId === nfCurrentRequestId) {
        nfSetCollectionLoading(false);
      }
    });
}

/* ----------------- MULTI-SELECT (fetch ALL pages, merge, then paginate client-side) ----------------- */

function nfLoadMultipleCollections(collections, sortBy, pushUrl, targetPage) {
  const gridContainer = document.querySelector("#ProductGridContainer");
  if (!gridContainer) return;

  nfCurrentRequestId++;
  const requestId = nfCurrentRequestId;

  nfSetCollectionLoading(true);

  const fetches = collections.map(function (collectionItem) {
    return nfFetchAllPagesForCollection(collectionItem, sortBy);
  });

  Promise.all(fetches)
    .then(function (results) {
      if (requestId !== nfCurrentRequestId) return;

      const mergedItems = [];
      const seenProducts = new Set();
      let fallbackHtml = "";

      results.forEach(function (result) {
        if (!fallbackHtml && result.fallbackHtml) {
          fallbackHtml = result.fallbackHtml;
        }

        result.items.forEach(function (item) {
          const productKey = nfGetProductKey(item);
          if (!productKey || seenProducts.has(productKey)) return;

          seenProducts.add(productKey);
          mergedItems.push(item);
        });
      });

      nfAllMergedItems = mergedItems;
      nfMergedFallbackHtml = fallbackHtml;

      const totalPages = Math.max(1, Math.ceil(nfAllMergedItems.length / NF_PAGE_SIZE));
      const page = Math.min(Math.max(1, targetPage || 1), totalPages);

      nfRenderMergedPage(page);

      const handles = collections.map(function (item) {
        return item.handle;
      });

      nfSetActiveCollections(handles);
      nfToggleResetButton();
      nfSetSortValue(sortBy);
      nfReapplyGridColumns();

      if (pushUrl) {
        nfUpdateUrl(handles, sortBy, page);
      }
    })
    .catch(function (error) {
      console.error("NF multi collection error:", error);
    })
    .finally(function () {
      if (requestId === nfCurrentRequestId) {
        nfSetCollectionLoading(false);
      }
    });
}

// Fetches page 1 of a collection, figures out how many pages it has,
// then fetches the rest in parallel. Returns ALL grid items for that collection.
function nfFetchAllPagesForCollection(collectionItem, sortBy) {
  return fetch(nfBuildCollectionFetchUrl(collectionItem.url, sortBy, 1), {
    method: "GET",
    headers: { Accept: "text/html" }
  })
    .then(function (response) {
      if (!response.ok) throw new Error("Collection fetch failed: " + collectionItem.handle);
      return response.text();
    })
    .then(function (firstPageHtml) {
      const firstDoc = new DOMParser().parseFromString(firstPageHtml, "text/html");
      const firstGrid = firstDoc.querySelector("#product-grid");
      const fallbackHtml = (firstDoc.querySelector("#ProductGridContainer") || {}).innerHTML || "";

      const firstItems = firstGrid
        ? Array.from(firstGrid.querySelectorAll(".grid__item"))
        : [];

      const totalPages = nfGetMaxPageFromDoc(firstDoc);

      if (totalPages <= 1) {
        return { items: firstItems, fallbackHtml: fallbackHtml };
      }

      const extraPageNumbers = [];
      for (let p = 2; p <= totalPages; p++) extraPageNumbers.push(p);

      const extraFetches = extraPageNumbers.map(function (pageNum) {
        return fetch(nfBuildCollectionFetchUrl(collectionItem.url, sortBy, pageNum), {
          method: "GET",
          headers: { Accept: "text/html" }
        })
          .then(function (res) {
            if (!res.ok) throw new Error("Collection page fetch failed: " + collectionItem.handle + " p" + pageNum);
            return res.text();
          })
          .then(function (html) {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const grid = doc.querySelector("#product-grid");
            return grid ? Array.from(grid.querySelectorAll(".grid__item")) : [];
          });
      });

      return Promise.all(extraFetches).then(function (extraItemsArrays) {
        const allItems = firstItems.concat.apply(firstItems, extraItemsArrays);
        return { items: allItems, fallbackHtml: fallbackHtml };
      });
    });
}

// Reads the theme's own pagination markup to find the highest page number listed.
function nfGetMaxPageFromDoc(doc) {
  const paginationLinks = doc.querySelectorAll(".pagination-wrapper a, .pagination a");
  if (!paginationLinks.length) return 1;

  let max = 1;

  paginationLinks.forEach(function (link) {
    let pageNum = null;

    try {
      const href = link.getAttribute("href");
      if (href) {
        const url = new URL(href, window.location.origin);
        const p = url.searchParams.get("page");
        if (p) pageNum = parseInt(p, 10);
      }
    } catch (err) {
      // ignore malformed hrefs
    }

    if (pageNum === null) {
      const text = (link.textContent || "").trim();
      if (/^\d+$/.test(text)) pageNum = parseInt(text, 10);
    }

    if (pageNum && pageNum > max) max = pageNum;
  });

  return max;
}

// Slices the cached merged list for the given page and renders it + our own pagination controls.
function nfRenderMergedPage(page) {
  const totalPages = Math.max(1, Math.ceil(nfAllMergedItems.length / NF_PAGE_SIZE));
  const clampedPage = Math.min(Math.max(1, page), totalPages);

  nfMergedCurrentPage = clampedPage;

  const start = (clampedPage - 1) * NF_PAGE_SIZE;
  const pageItems = nfAllMergedItems.slice(start, start + NF_PAGE_SIZE);

  nfRenderMergedProducts(pageItems, nfMergedFallbackHtml);
  nfRenderMergedPagination(clampedPage, totalPages);
}

function nfRenderMergedProducts(items, fallbackHtml) {
  const gridContainer = document.querySelector("#ProductGridContainer");
  if (!gridContainer) return;

  const currentGrid = document.querySelector("#product-grid");

  if (!currentGrid) {
    gridContainer.innerHTML = fallbackHtml || "";
    return;
  }

  if (items.length === 0) {
    gridContainer.innerHTML =
      '<div class="collection collection--empty page-width" id="product-grid">' +
      '<div class="title-wrapper center">' +
      '<h2 class="title title--primary">No products found</h2>' +
      "</div>" +
      "</div>";
    return;
  }

  currentGrid.innerHTML = "";

  items.forEach(function (item) {
    currentGrid.appendChild(item.cloneNode(true));
  });
}

// Builds our own pagination bar for merged/multi-select results, matching the
// theme's own native pagination markup exactly (classes, aria-labels, icon)
// so it inherits the site's existing pagination CSS.
const NF_PAGINATION_ARROW_SVG =
  '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path></svg></span>';

function nfRenderMergedPagination(currentPage, totalPages) {
  let wrapper = document.querySelector(".pagination-wrapper");

  if (totalPages <= 1) {
    if (wrapper) wrapper.style.display = "none";
    return;
  }

  if (!wrapper) {
    wrapper = document.createElement("div");

    const gridContainer = document.querySelector("#ProductGridContainer");
    if (gridContainer) gridContainer.appendChild(wrapper);
  }

  wrapper.className = "pagination-wrapper";
  wrapper.setAttribute("data-page", currentPage);
  wrapper.style.display = "";

  let items = "";

  if (currentPage > 1) {
    items +=
      '<li><a href="' + nfBuildMergedPageHref(currentPage - 1) +
      '" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="הקודם" data-nf-merged-page="' + (currentPage - 1) + '">' +
      NF_PAGINATION_ARROW_SVG +
      "</a></li>";
  }

  for (let i = 1; i <= totalPages; i++) {
    if (i === currentPage) {
      items +=
        '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="עמוד ' + i + '">' + i + "</a></li>";
    } else {
      items +=
        '<li><a href="' + nfBuildMergedPageHref(i) +
        '" class="pagination__item link" aria-label="עמוד ' + i + '" data-nf-merged-page="' + i + '">' + i + "</a></li>";
    }
  }

  if (currentPage < totalPages) {
    items +=
      '<li><a href="' + nfBuildMergedPageHref(currentPage + 1) +
      '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="הבא" data-nf-merged-page="' + (currentPage + 1) + '">' +
      NF_PAGINATION_ARROW_SVG +
      "</a></li>";
  }

  wrapper.innerHTML =
    '<nav class="pagination" role="navigation" aria-label="עמודים">' +
    '<ul class="pagination__list list-unstyled" role="list">' +
    items +
    "</ul></nav>";
}

// Builds a full, shareable href (keeps nf_collections + sort_by + page together)
// so the link is correct even if a click ever bypasses our JS handler
// (opened in a new tab, middle-click, etc).
function nfBuildMergedPageHref(page) {
  const url = new URL(window.location.href);
  const handles = nfSelectedCollections.map(function (item) {
    return item.handle;
  });

  if (handles.length) {
    url.searchParams.set("nf_collections", handles.join(","));
  } else {
    url.searchParams.delete("nf_collections");
  }

  const sortBy = nfGetCurrentSortValue();
  if (sortBy) {
    url.searchParams.set("sort_by", sortBy);
  } else {
    url.searchParams.delete("sort_by");
  }

  if (page > 1) {
    url.searchParams.set("page", page);
  } else {
    url.searchParams.delete("page");
  }

  return url.pathname + url.search;
}

function nfGetProductKey(item) {
  const productLink = item.querySelector('a[href*="/products/"]');

  if (!productLink) return "";

  const url = new URL(productLink.getAttribute("href"), window.location.origin);

  return url.pathname;
}

function nfLoadFromUrl(pushUrl) {
  const params = new URLSearchParams(window.location.search);
  const collectionsParam = params.get("nf_collections");
  const oldSingleParam = params.get("nf_collection");
  const sortBy = params.get("sort_by") || "";
  const page = parseInt(params.get("page"), 10) || 1;

  nfSetSortValue(sortBy);

  let handles = [];

  if (collectionsParam) {
    handles = collectionsParam
      .split(",")
      .map(function (handle) {
        return handle.trim();
      })
      .filter(Boolean);
  } else if (oldSingleParam) {
    handles = [oldSingleParam];
  }

  nfSelectedCollections = handles
    .map(function (handle) {
      const link = nfGetCollectionLinkByHandle(handle);

      if (!link) return null;

      return {
        handle: handle,
        url: link.dataset.collectionUrl
      };
    })
    .filter(Boolean);

  if (nfSelectedCollections.length > 0) {
    nfLoadMultipleCollections(nfSelectedCollections, sortBy, pushUrl, page);
  } else {
    nfLoadBaseCollection(false, sortBy, page);
  }
}

function nfBuildCollectionFetchUrl(collectionUrl, sortBy, page) {
  const currentUrl = new URL(window.location.href);
  const url = new URL(collectionUrl, window.location.origin);

  // Preserve all current query parameters
  currentUrl.searchParams.forEach((value, key) => {
    if (key === "page") return; // page is set explicitly below
    if (key !== "t") {
      url.searchParams.set(key, value);
    }
  });

  // Update sort if changed
  if (sortBy) {
    url.searchParams.set("sort_by", sortBy);
  } else {
    url.searchParams.delete("sort_by");
  }

  if (page && page > 1) {
    url.searchParams.set("page", page);
  } else {
    url.searchParams.delete("page");
  }

  // Prevent cache
  url.searchParams.set("t", Date.now());

  return url.toString();
}

function nfUpdateUrl(handles, sortBy, page) {
  const url = new URL(window.location.href);

  if (handles.length) {
    url.searchParams.set("nf_collections", handles.join(","));
  } else {
    url.searchParams.delete("nf_collections");
    url.searchParams.delete("nf_collection");
  }

  if (sortBy) {
    url.searchParams.set("sort_by", sortBy);
  } else {
    url.searchParams.delete("sort_by");
  }

  if (page && page > 1) {
    url.searchParams.set("page", page);
  } else {
    url.searchParams.delete("page");
  }

  window.history.pushState({}, "", url.pathname + url.search);
}

function nfGetCurrentSortValue() {
  const sortSelect = document.querySelector("#SortBy, .facet-filters__sort");
  return sortSelect ? sortSelect.value : "";
}

function nfSetSortValue(sortBy) {
  const sortSelect = document.querySelector("#SortBy, .facet-filters__sort");
  if (!sortSelect || !sortBy) return;

  sortSelect.value = sortBy;
}

function nfGetCollectionLinkByHandle(handle) {
  return document.querySelector(
    '[data-nf-collection-link][data-collection-handle="' + handle + '"]'
  );
}

function nfSetCollectionLoading(isLoading) {
  const gridContainer = document.querySelector("#ProductGridContainer");
  if (!gridContainer) return;

  if (isLoading) {
    gridContainer.classList.add("nf-loading-products");
  } else {
    gridContainer.classList.remove("nf-loading-products");
  }
}

function nfSetActiveCollections(handles) {
  document.querySelectorAll("[data-nf-collection-link]").forEach(function (link) {
    if (handles.includes(link.dataset.collectionHandle)) {
      link.classList.add("active");
    } else {
      link.classList.remove("active");
    }
  });
}

function nfToggleResetButton() {
  const resetButton = document.querySelector("[data-nf-reset-collection]");
  if (!resetButton) return;

  if (nfSelectedCollections.length > 0) {
    resetButton.hidden = false;
  } else {
    resetButton.hidden = true;
  }
}

function nfClearCollections(pushUrl) {
  const sortBy = nfGetCurrentSortValue();

  nfSelectedCollections = [];
  nfAllMergedItems = [];
  nfMergedFallbackHtml = "";
  nfMergedCurrentPage = 1;

  nfLoadBaseCollection(pushUrl, sortBy);
}

function nfReapplyGridColumns() {
  const grid = document.querySelector("#product-grid");
  const activeGridButton = document.querySelector(".nf-grid-btn.active");

  if (!grid || !activeGridButton) return;

  const cols = activeGridButton.dataset.grid || "3";

  grid.style.gridTemplateColumns = "repeat(" + cols + ", minmax(0,1fr))";
}

/* Mobile filter drawer */
function nfOpenMobileFilter() {
  document.body.classList.add("nf-filter-open");
}

function nfCloseMobileFilter() {
  document.body.classList.remove("nf-filter-open");
}

/* Pagination clicks - branch depending on whether we're in merged (multi-select) mode or plain base-collection mode */
document.addEventListener("click", function (e) {
  const mergedLink = e.target.closest("[data-nf-merged-page]");

  if (mergedLink) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const page = parseInt(mergedLink.dataset.nfMergedPage, 10) || 1;
    nfRenderMergedPage(page);

    const handles = nfSelectedCollections.map(function (item) {
      return item.handle;
    });

    nfUpdateUrl(handles, nfGetCurrentSortValue(), page);

    // Scroll back to the top of the product grid (not wherever the click happened,
    // e.g. near the bottom where the pagination links live), offset below the
    // sticky header so it isn't hidden underneath it
    nfScrollToGridTop();

    return;
  }

  const link = e.target.closest(".pagination a");
  if (!link) return;

  // Only reachable for the base collection's own native pagination now,
  // since merged results always use [data-nf-merged-page] links above.
  if (nfSelectedCollections.length > 0) {
    e.preventDefault();
    return;
  }

  e.preventDefault();

  const url = new URL(link.href);
  nfLoadPage(url);
});

function nfLoadPage(url) {
  nfSetCollectionLoading(true);

  fetch(url.toString(), {
    headers: {
      Accept: "text/html"
    }
  })
    .then((res) => res.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newGrid = doc.querySelector("#ProductGridContainer");

      if (!newGrid) return;

      document.querySelector("#ProductGridContainer").innerHTML = newGrid.innerHTML;

      window.history.pushState({}, "", url.pathname + url.search);

      nfReapplyGridColumns();

      // Scroll back to the top of the product grid here too, for consistency
      nfScrollToGridTop();
    })
    .finally(() => {
      nfSetCollectionLoading(false);
    });
}

// Detects any currently-pinned sticky/fixed header and returns its height,
// so we can offset our scroll target below it instead of hiding behind it.
function nfGetStickyOffset() {
  let offset = 0;

  document.querySelectorAll("body *").forEach(function (el) {
    if (el.offsetHeight === 0 && el.offsetWidth === 0) return;

    const style = window.getComputedStyle(el);
    if (style.position !== "fixed" && style.position !== "sticky") return;

    const rect = el.getBoundingClientRect();
    // Only count elements currently pinned at (or very near) the top of the viewport
    if (rect.top <= 5 && rect.bottom > 0) {
      offset = Math.max(offset, rect.bottom);
    }
  });

  return offset;
}

// Scrolls so the top of the product grid lands just below any sticky header,
// instead of hidden behind it.
function nfScrollToGridTop() {
  const gridContainer = document.querySelector("#ProductGridContainer");

  if (!gridContainer) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  const stickyOffset = nfGetStickyOffset();
  const extraBreathingRoom = 4;
  const targetY =
    gridContainer.getBoundingClientRect().top +
    window.pageYOffset -
    stickyOffset -
    extraBreathingRoom;

  window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
}

function nfTogglePagination() {
  const pagination = document.querySelector(".pagination-wrapper");

  if (!pagination) return;

  const pageLinks = pagination.querySelectorAll(".pagination__item");

  if (pageLinks.length <= 1) {
    pagination.style.display = "none";
  } else {
    pagination.style.display = "";
  }
}